import { useState, useEffect, useContext } from "react";
import { SearchContext, KBContext } from "../index";
import { articleSearch } from "../../Service/ArticleService";
import { getAiSearchResponse } from "../../services/aiSearch";
import { useSearchParams } from "react-router-dom";
import { debounce, apiWithAbort } from "../../Constants/utility";
import { handleGainSightEventForSearchResults, KB_WEBSITE_GAINSIGHT_EVENTS, sendGainsightEvent } from "../../gainSightIntegration";
import { isFeatureEnabled } from "../../utils";
import { FTR_KB_AI_SEARCH } from "../../Constants";

const searchWithAbort = apiWithAbort(articleSearch);

const getSearchArticles = ({ searchTerm, product, isAISearchEnabled, onLoad, onSuccess }) => {
  const pathParams = {
    searchTerm,
    ...(product && { product })
  };

  if (searchTerm.length >= 2) {
    sendGainsightEvent(KB_WEBSITE_GAINSIGHT_EVENTS.KB_SEARCH_SUBMITTED, { search_query: searchTerm });
  }

  const startTime = performance.now();
  onLoad(true);

  if (isAISearchEnabled) {
    // New AI-based search
    getAiSearchResponse({ query: searchTerm }).then((result) => {
      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);
      const articles = result?.search_results || [];

      onSuccess(articles);
      if (articles.length > 0) {
        const top3Slugs = articles.slice(0, 3).map(article => article.slug);
        handleGainSightEventForSearchResults({
          search_query: searchTerm,
          results_count: articles.length,
          top_result_id: articles[0]?.slug || "",
          top_3_result_slugs: top3Slugs,
          search_latency_ms: latency,
        });
      } else {
        sendGainsightEvent(KB_WEBSITE_GAINSIGHT_EVENTS.KB_SEARCH_ZERO_RESULTS, { search_query: searchTerm });
      }
    }).catch((err) => {
      console.error("[SearchContext] AI Search failed:", err);
      onSuccess([]);
    }).finally(() => {
      onLoad(false);
    });
  } else {
    // Legacy search
    searchWithAbort(searchTerm, pathParams).then((result) => {
      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);
      const raw = result?.data?.data?.articles ?? result?.data?.articles ?? [];
      const articles = Array.isArray(raw) ? raw : [];

      onSuccess(articles);
      if (articles.length > 0) {
        const top3Slugs = articles.slice(0, 3).map(article => article.slug);
        handleGainSightEventForSearchResults({
          search_query: searchTerm,
          results_count: articles.length,
          top_result_id: articles[0]?.slug || "",
          top_3_result_slugs: top3Slugs,
          search_latency_ms: latency,
        });
      } else {
        sendGainsightEvent(KB_WEBSITE_GAINSIGHT_EVENTS.KB_SEARCH_ZERO_RESULTS, { search_query: searchTerm });
      }
    }).catch((err) => {
      console.warn(err);
      onSuccess([]);
    }).finally(() => {
      onLoad(false);
    });
  }
};

const debouncedSearch = debounce(getSearchArticles, 300);

export default function ArticleProvider(props) {
  const [searchList, setSearchList] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const productParam = searchParams.get('product');

  const { availableFeatures } = useContext(KBContext);
  const isAISearchEnabled = isFeatureEnabled(FTR_KB_AI_SEARCH, availableFeatures);

  // Match hiver-website-old: depend only on searchInput/productParam so debounce isn't reset when isAISearchEnabled loads (keeps legacy search working when FF is off).
  useEffect(() => {
    setLoading(false);
    if (searchInput.length === 0) {
      setSearchList(false);
    } else if (searchInput.length > 0) {
      debouncedSearch({ searchTerm: searchInput, product: productParam, isAISearchEnabled, onLoad: setLoading, onSuccess: setSearchList });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isAISearchEnabled intentionally omitted so debounce is not reset when features load
  }, [searchInput, productParam]);

  return (
    <SearchContext.Provider
      value={{
        searchList,
        setSearchInput,
        setSearchList,
        searchInput,
        triggerSearch: () => debouncedSearch({ searchTerm: searchInput, product: productParam, isAISearchEnabled, onLoad: setLoading, onSuccess: setSearchList }),
        loading,
      }}
    >
      {props.children}
    </SearchContext.Provider>
  );
}
